import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Play, Pause, SkipBack, SkipForward, Plus, Minus, 
  ChevronDown, ChevronRight, Repeat, User, Diamond, Trash2,
  Circle, CircleDot, Move, RotateCw, Maximize2, Eye, Camera, Sun, Lightbulb
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const DEFAULT_FPS = 24;
const PIXELS_PER_FRAME = 4;
const TRACK_LABEL_WIDTH = 160; // Fixed width for track labels

// Track types for character transforms
const TRANSFORM_TRACKS = [
  { id: 'position', name: 'Position', icon: Move, color: 'text-blue-400', bgColor: 'bg-blue-900/30' },
  { id: 'rotation', name: 'Rotation', icon: RotateCw, color: 'text-green-400', bgColor: 'bg-green-900/30' },
  { id: 'scale', name: 'Scale', icon: Maximize2, color: 'text-purple-400', bgColor: 'bg-purple-900/30' },
  { id: 'visibility', name: 'Visibility', icon: Eye, color: 'text-cyan-400', bgColor: 'bg-cyan-900/30' },
];

// Shot-level tracks (independent of characters/clips)
const SHOT_TRACKS = [
  { id: 'camera', name: 'Camera', icon: Camera, color: 'text-amber-400', bgColor: 'bg-amber-900/30' },
  { id: 'light', name: 'Light', icon: Sun, color: 'text-yellow-400', bgColor: 'bg-yellow-900/30' },
];

// ============== TIMELINE RULER ==============
function TimelineRuler({ totalFrames, fps, currentFrame, pixelsPerFrame, onFrameClick }) {
  const rulerRef = useRef(null);
  
  const ticks = [];
  const majorTickInterval = fps;
  const minorTickInterval = Math.max(1, Math.floor(fps / 4));
  
  for (let frame = 0; frame <= totalFrames; frame += minorTickInterval) {
    const isMajor = frame % majorTickInterval === 0;
    const seconds = frame / fps;
    
    ticks.push({
      frame,
      isMajor,
      label: isMajor ? `${seconds.toFixed(0)}s` : null,
      x: frame * pixelsPerFrame
    });
  }
  
  const handleClick = (e) => {
    if (!rulerRef.current) return;
    const rect = rulerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frame = Math.round(x / pixelsPerFrame);
    onFrameClick(Math.max(0, Math.min(totalFrames - 1, frame)));
  };
  
  return (
    <div 
      ref={rulerRef}
      className="h-6 bg-slate-800 border-b border-slate-700 relative cursor-pointer"
      onClick={handleClick}
      style={{ minWidth: totalFrames * pixelsPerFrame }}
    >
      <svg className="absolute inset-0 pointer-events-none" width={totalFrames * pixelsPerFrame + 50} height="24">
        {ticks.map(({ frame, isMajor, label, x }) => (
          <g key={frame}>
            <line
              x1={x} y1={isMajor ? 0 : 12} x2={x} y2={24}
              stroke={isMajor ? '#64748b' : '#475569'}
              strokeWidth={isMajor ? 1 : 0.5}
            />
            {label && (
              <text x={x + 3} y={10} fill="#94a3b8" fontSize="9" fontFamily="monospace">
                {label}
              </text>
            )}
          </g>
        ))}
      </svg>
      
      {/* Playhead - using transform for GPU acceleration */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-red-500 pointer-events-none z-10 will-change-transform"
        style={{ transform: `translateX(${currentFrame * pixelsPerFrame}px)` }}
      >
        <div className="absolute -top-0.5 -left-1.5 w-3 h-3 bg-red-500 rounded-sm rotate-45" />
      </div>
    </div>
  );
}

// ============== ANIMATION CLIP ==============
function AnimationClip({ 
  clip, 
  pixelsPerFrame, 
  isSelected, 
  onClick, 
  onDoubleClick,
  onDragEnd,
  onResizeEnd 
}) {
  const clipRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  
  const clipWidth = clip.duration * pixelsPerFrame;
  const clipLeft = clip.startFrame * pixelsPerFrame;
  
  const getClipColor = () => {
    if (clip.color) return clip.color;
    if (clip.animation?.name?.toLowerCase().includes('walk')) return 'bg-emerald-600';
    if (clip.animation?.name?.toLowerCase().includes('run')) return 'bg-amber-600';
    if (clip.animation?.name?.toLowerCase().includes('idle')) return 'bg-blue-600';
    return 'bg-purple-600';
  };

  // Handle single click - just select the clip
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    
    console.log('AnimationClip handleClick called for clip:', clip.id);
    onClick(clip);
  }, [clip, onClick]);

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) return;
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const rect = clipRef.current.getBoundingClientRect();
    const offset = e.clientX - rect.left;
    
    const handleMouseMoveStart = (moveEvent) => {
      const dx = Math.abs(moveEvent.clientX - startX);
      const dy = Math.abs(moveEvent.clientY - startY);
      
      if (dx > 5 || dy > 5) {
        setIsDragging(true);
        setDragOffset(offset);
        document.removeEventListener('mousemove', handleMouseMoveStart);
        document.removeEventListener('mouseup', handleMouseUpStart);
      }
    };
    
    const handleMouseUpStart = () => {
      document.removeEventListener('mousemove', handleMouseMoveStart);
      document.removeEventListener('mouseup', handleMouseUpStart);
    };
    
    document.addEventListener('mousemove', handleMouseMoveStart);
    document.addEventListener('mouseup', handleMouseUpStart);
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e) => {
      if (isDragging && clipRef.current) {
        const parent = clipRef.current.parentElement;
        const parentRect = parent.getBoundingClientRect();
        const newLeft = e.clientX - parentRect.left - dragOffset;
        const newFrame = Math.max(0, Math.round(newLeft / pixelsPerFrame));
        clipRef.current.style.left = `${newFrame * pixelsPerFrame}px`;
      }
      
      if (isResizing && clipRef.current) {
        const parent = clipRef.current.parentElement;
        const parentRect = parent.getBoundingClientRect();
        const clipStartX = clip.startFrame * pixelsPerFrame;
        const newWidth = e.clientX - parentRect.left - clipStartX;
        const newDuration = Math.max(12, Math.round(newWidth / pixelsPerFrame));
        clipRef.current.style.width = `${newDuration * pixelsPerFrame}px`;
      }
    };

    const handleMouseUp = (e) => {
      if (isDragging && clipRef.current) {
        const parent = clipRef.current.parentElement;
        const parentRect = parent.getBoundingClientRect();
        const newLeft = e.clientX - parentRect.left - dragOffset;
        const newFrame = Math.max(0, Math.round(newLeft / pixelsPerFrame));
        onDragEnd(clip.id, newFrame);
      }
      
      if (isResizing && clipRef.current) {
        const parent = clipRef.current.parentElement;
        const parentRect = parent.getBoundingClientRect();
        const clipStartX = clip.startFrame * pixelsPerFrame;
        const newWidth = e.clientX - parentRect.left - clipStartX;
        const newDuration = Math.max(12, Math.round(newWidth / pixelsPerFrame));
        onResizeEnd(clip.id, newDuration);
      }
      
      setIsDragging(false);
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, clip, pixelsPerFrame, onDragEnd, onResizeEnd]);

  // Handle native double-click event
  const handleNativeDoubleClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    console.log('Native double-click on clip:', clip.id);
    onDoubleClick(clip);
  }, [clip, onDoubleClick]);

  return (
    <div
      ref={clipRef}
      className={`absolute top-1 bottom-1 rounded cursor-pointer transition-shadow z-10 ${getClipColor()}
        ${isSelected ? 'ring-2 ring-white shadow-lg z-20' : 'hover:ring-1 hover:ring-white/50'}
        ${isDragging ? 'opacity-80 cursor-grabbing z-30' : ''}`}
      style={{ left: clipLeft, width: clipWidth, minWidth: 48 }}
      onClick={handleClick}
      onDoubleClick={handleNativeDoubleClick}
      onMouseDown={handleMouseDown}
      data-testid={`clip-${clip.id}`}
    >
      <div className="h-full flex items-center px-2 overflow-hidden pointer-events-none">
        <span className="text-xs font-medium text-white truncate">
          {clip.animation?.name || 'Clip'}
        </span>
        {clip.loops > 1 && (
          <div className="flex items-center ml-1 text-white/70">
            <Repeat className="w-3 h-3" />
            <span className="text-xs ml-0.5">{clip.loops}</span>
          </div>
        )}
      </div>
      
      {/* Resize handle */}
      <div 
        className="resize-handle absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/20 rounded-r"
        onMouseDown={handleResizeMouseDown}
      />
      
      {/* Loop dividers */}
      {clip.loops > 1 && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded">
          {Array.from({ length: clip.loops - 1 }).map((_, i) => (
            <div 
              key={i}
              className="absolute top-0 bottom-0 w-px bg-white/30"
              style={{ left: `${((i + 1) / clip.loops) * 100}%` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============== KEYFRAME DIAMOND (draggable) ==============
function KeyframeDiamond({ 
  keyframe, 
  boneName,
  displayFrame, 
  pixelsPerFrame,
  clipSpeed = 1, // Speed affects drag scaling
  isSelected, 
  isAtPlayhead,
  onSelect,
  onDrag,
  onDelete
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartFrame, setDragStartFrame] = useState(0);
  
  const handleMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartFrame(keyframe.frame);
    onSelect(keyframe, boneName);
  };
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartX;
      // Convert pixel delta to display frames, then scale by speed to get keyframe frames
      const deltaDisplayFrames = deltaX / pixelsPerFrame;
      const deltaKeyframeFrames = Math.round(deltaDisplayFrames * clipSpeed);
      const newFrame = Math.max(0, dragStartFrame + deltaKeyframeFrames);
      onDrag(keyframe.id, boneName, newFrame);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStartX, dragStartFrame, pixelsPerFrame, clipSpeed, keyframe.id, boneName, onDrag]);
  
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(keyframe.id, boneName);
  };
  
  return (
    <div
      className={`absolute top-1/2 -translate-y-1/2 cursor-pointer group z-10
        ${isDragging ? 'z-20' : ''}`}
      style={{ left: displayFrame * pixelsPerFrame - 5 }}
      onMouseDown={handleMouseDown}
      onContextMenu={handleContextMenu}
      title={`Frame ${keyframe.frame} - Drag to move, Right-click to delete`}
    >
      <div className={`w-2.5 h-2.5 rotate-45 transition-all
        ${isSelected ? 'bg-white ring-2 ring-purple-400 scale-125' : ''}
        ${isAtPlayhead ? 'bg-yellow-400' : isSelected ? 'bg-white' : 'bg-purple-400'}
        ${!isSelected && !isAtPlayhead ? 'group-hover:bg-purple-300 group-hover:scale-110' : ''}
      `} />
    </div>
  );
}

// ============== BONE TRACK (for expanded clip view) ==============
function BoneTrack({ 
  boneName, 
  keyframes, 
  clipStartFrame, 
  clipDuration,
  clipSpeed = 1, // Speed affects keyframe display positions
  totalFrames, 
  pixelsPerFrame, 
  currentFrame,
  selectedKeyframe,
  onSelectKeyframe,
  onDragKeyframe,
  onDeleteKeyframe,
  onAddKeyframe
}) {
  const trackRef = useRef(null);
  
  // Filter keyframes for this bone and adjust to clip position
  // Speed affects display: at 2x speed, keyframe at frame 24 displays at position 12
  const boneKeyframes = keyframes
    .filter(kf => kf.bones && kf.bones[boneName])
    .map(kf => ({
      ...kf,
      displayFrame: clipStartFrame + (kf.frame / clipSpeed)  // Scale by speed for display
    }));
  
  // Double-click to add keyframe at position
  const handleDoubleClick = (e) => {
    if (!trackRef.current || !onAddKeyframe) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clickedFrame = Math.round(x / pixelsPerFrame);
    // Convert to local frame (relative to clip start), then to keyframe space (multiply by speed)
    const localFrame = clickedFrame - clipStartFrame;
    const keyframeFrame = Math.round(localFrame * clipSpeed); // Convert display position to keyframe frame
    if (localFrame >= 0 && localFrame < clipDuration) {
      onAddKeyframe(boneName, keyframeFrame);
    }
  };
  
  return (
    <div className="flex h-6 border-b border-slate-800">
      <div 
        ref={trackRef}
        className="flex-1 relative bg-slate-900/30 cursor-crosshair" 
        style={{ minWidth: totalFrames * pixelsPerFrame }}
        onDoubleClick={handleDoubleClick}
        title="Double-click to add keyframe"
      >
        {/* Keyframe diamonds */}
        {boneKeyframes.map((kf, idx) => (
          <KeyframeDiamond
            key={`${kf.id || idx}-${kf.frame}`}
            keyframe={kf}
            boneName={boneName}
            displayFrame={kf.displayFrame}
            pixelsPerFrame={pixelsPerFrame}
            clipSpeed={clipSpeed}
            isSelected={selectedKeyframe?.id === kf.id && selectedKeyframe?.boneName === boneName}
            isAtPlayhead={Math.abs(currentFrame - kf.displayFrame) < 0.5}
            onSelect={onSelectKeyframe}
            onDrag={onDragKeyframe}
            onDelete={onDeleteKeyframe}
          />
        ))}
        
        {/* Clip region highlight */}
        <div 
          className="absolute top-0 bottom-0 bg-purple-500/10 border-l border-r border-purple-500/30 pointer-events-none"
          style={{ 
            left: clipStartFrame * pixelsPerFrame,
            width: clipDuration * pixelsPerFrame
          }}
        />
        
        {/* Playhead indicator */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-red-500/50 pointer-events-none will-change-transform"
          style={{ transform: `translateX(${currentFrame * pixelsPerFrame}px)` }}
        />
      </div>
    </div>
  );
}

// ============== Y OFFSET TRACK ==============
function YOffsetTrack({ 
  yOffsetKeyframes = [],
  clipStartFrame, 
  clipDuration,
  clipSpeed = 1,
  totalFrames, 
  pixelsPerFrame, 
  currentFrame,
  isSelected,
  onSelect,
  onAddKeyframe,
  onSelectKeyframe,
  selectedKeyframe,
  onDeleteKeyframe,
  onUpdateKeyframe
}) {
  const trackRef = useRef(null);
  const [draggingKeyframe, setDraggingKeyframe] = useState(null);
  const [editingKeyframe, setEditingKeyframe] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Map keyframes to display positions
  const displayKeyframes = yOffsetKeyframes.map(kf => ({
    ...kf,
    displayFrame: clipStartFrame + (kf.frame / clipSpeed)
  }));
  
  const handleClick = (e) => {
    e.stopPropagation();
    setEditingKeyframe(null);
    onSelect?.();
  };
  
  const handleDoubleClick = (e) => {
    if (!trackRef.current || !onAddKeyframe) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clickedFrame = Math.round(x / pixelsPerFrame);
    const localFrame = clickedFrame - clipStartFrame;
    const keyframeFrame = Math.round(localFrame * clipSpeed);
    if (localFrame >= 0 && localFrame < clipDuration) {
      onAddKeyframe(keyframeFrame, 0); // Default offset of 0
    }
  };
  
  // Keyframe dragging
  const handleKeyframeDragStart = (e, kf) => {
    e.stopPropagation();
    setDraggingKeyframe(kf);
  };
  
  const handleKeyframeDrag = useCallback((e) => {
    if (!draggingKeyframe || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newDisplayFrame = Math.round(x / pixelsPerFrame);
    const newLocalFrame = newDisplayFrame - clipStartFrame;
    const newFrame = Math.max(0, Math.min(Math.round(newLocalFrame * clipSpeed), clipDuration * clipSpeed));
    
    if (newFrame !== draggingKeyframe.frame && onUpdateKeyframe) {
      onUpdateKeyframe(draggingKeyframe.frame, newFrame, draggingKeyframe.offset);
    }
  }, [draggingKeyframe, clipStartFrame, clipSpeed, clipDuration, pixelsPerFrame, onUpdateKeyframe]);
  
  const handleKeyframeDragEnd = useCallback(() => {
    setDraggingKeyframe(null);
  }, []);
  
  useEffect(() => {
    if (draggingKeyframe) {
      window.addEventListener('mousemove', handleKeyframeDrag);
      window.addEventListener('mouseup', handleKeyframeDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleKeyframeDrag);
        window.removeEventListener('mouseup', handleKeyframeDragEnd);
      };
    }
  }, [draggingKeyframe, handleKeyframeDrag, handleKeyframeDragEnd]);
  
  // Inline editing
  const handleKeyframeDoubleClick = (e, kf) => {
    e.stopPropagation();
    setEditingKeyframe(kf);
    setEditValue(kf.offset.toString());
  };
  
  const handleEditSubmit = () => {
    if (editingKeyframe && onUpdateKeyframe) {
      const newOffset = parseFloat(editValue) || 0;
      onUpdateKeyframe(editingKeyframe.frame, editingKeyframe.frame, newOffset);
    }
    setEditingKeyframe(null);
  };
  
  return (
    <div className={`flex h-6 border-b border-slate-800 ${isSelected ? 'ring-1 ring-inset ring-white/30' : ''}`}>
      <div 
        ref={trackRef}
        className="flex-1 relative bg-amber-900/10 cursor-pointer"
        style={{ minWidth: totalFrames * pixelsPerFrame }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        title="Y Offset track - Click to select, Double-click to add keyframe"
      >
        {/* Y Offset keyframe diamonds */}
        {displayKeyframes.map((kf, idx) => (
          <div
            key={`yoffset-${kf.frame}-${idx}`}
            className={`absolute top-1/2 -translate-y-1/2 z-10 cursor-grab
              ${draggingKeyframe?.frame === kf.frame ? 'cursor-grabbing' : ''}`}
            style={{ left: kf.displayFrame * pixelsPerFrame - 5 }}
            title={`Frame ${kf.frame}: Y Offset = ${kf.offset} (drag to move, double-click to edit)`}
            onClick={(e) => {
              e.stopPropagation();
              if (editingKeyframe) return; // Don't select when editing
              onSelectKeyframe?.({ id: `yoffset-${kf.frame}`, type: 'yOffset', frame: kf.frame, offset: kf.offset });
            }}
            onMouseDown={(e) => {
              // Don't start drag if we're in edit mode
              if (editingKeyframe) {
                e.stopPropagation();
                return;
              }
              handleKeyframeDragStart(e, kf);
            }}
            onDoubleClick={(e) => handleKeyframeDoubleClick(e, kf)}
          >
            <div className={`w-2.5 h-2.5 rotate-45 bg-amber-400 hover:bg-amber-300 hover:scale-110 transition-all
              ${selectedKeyframe?.id === `yoffset-${kf.frame}` ? 'ring-2 ring-white scale-125' : ''}`} 
            />
            
            {/* Inline editor popup */}
            {editingKeyframe?.frame === kf.frame && (
              <div 
                className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-slate-800 border border-slate-600 rounded p-2 shadow-lg z-50"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <label className="text-xs text-slate-400 block mb-1">Y Offset</label>
                <input
                  type="number"
                  step="0.01"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') handleEditSubmit();
                    if (e.key === 'Escape') setEditingKeyframe(null);
                  }}
                  className="w-24 h-7 text-sm bg-slate-700 border border-slate-500 rounded px-2 text-white focus:outline-none focus:ring-1 focus:ring-amber-400"
                  autoFocus
                />
                <div className="flex gap-1 mt-2">
                  <button onClick={handleEditSubmit} className="flex-1 py-1 text-xs bg-green-600 rounded hover:bg-green-500">Apply</button>
                  <button 
                    onClick={() => {
                      onDeleteKeyframe?.(kf.frame);
                      setEditingKeyframe(null);
                    }} 
                    className="flex-1 py-1 text-xs bg-red-600 rounded hover:bg-red-500"
                  >Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
        
        {/* Clip region highlight */}
        <div 
          className="absolute top-0 bottom-0 bg-amber-500/10 border-l border-r border-amber-500/30 pointer-events-none"
          style={{ 
            left: clipStartFrame * pixelsPerFrame,
            width: clipDuration * pixelsPerFrame
          }}
        />
        
        {/* Playhead indicator */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-red-500/50 pointer-events-none will-change-transform"
          style={{ transform: `translateX(${currentFrame * pixelsPerFrame}px)` }}
        />
      </div>
    </div>
  );
}

// ============== TRANSFORM TRACK (Position, Rotation, Scale, Visibility) ==============
function TransformTrack({ 
  trackType, // 'position' | 'rotation' | 'scale' | 'visibility'
  keyframes = [],
  clipStartFrame, 
  clipDuration,
  clipSpeed = 1,
  totalFrames, 
  pixelsPerFrame, 
  currentFrame,
  isSelected,
  onSelect,
  onAddKeyframe,
  onSelectKeyframe,
  selectedKeyframe,
  onUpdateKeyframe,
  onDeleteKeyframe
}) {
  const trackRef = useRef(null);
  const trackConfig = TRANSFORM_TRACKS.find(t => t.id === trackType) || TRANSFORM_TRACKS[0];
  const [draggingKeyframe, setDraggingKeyframe] = useState(null);
  const [editingKeyframe, setEditingKeyframe] = useState(null);
  const [editValues, setEditValues] = useState({});
  
  // Filter keyframes for this track type
  const trackKeyframes = keyframes
    .filter(kf => kf.trackType === trackType || (kf.transforms && kf.transforms[trackType]))
    .map(kf => ({
      ...kf,
      displayFrame: clipStartFrame + (kf.frame / clipSpeed),
      value: kf.value || kf.transforms?.[trackType]
    }));
  
  const handleClick = (e) => {
    e.stopPropagation();
    setEditingKeyframe(null);
    onSelect(trackType);
  };
  
  const handleDoubleClick = (e) => {
    if (!trackRef.current || !onAddKeyframe) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clickedFrame = Math.round(x / pixelsPerFrame);
    const localFrame = clickedFrame - clipStartFrame;
    const keyframeFrame = Math.round(localFrame * clipSpeed);
    if (localFrame >= 0 && localFrame < clipDuration) {
      onAddKeyframe(trackType, keyframeFrame);
    }
  };
  
  // Keyframe dragging
  const handleKeyframeDragStart = (e, kf) => {
    e.stopPropagation();
    setDraggingKeyframe(kf);
  };
  
  const handleKeyframeDrag = useCallback((e) => {
    if (!draggingKeyframe || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newDisplayFrame = Math.round(x / pixelsPerFrame);
    const newLocalFrame = newDisplayFrame - clipStartFrame;
    const newFrame = Math.max(0, Math.min(Math.round(newLocalFrame * clipSpeed), clipDuration * clipSpeed));
    
    if (newFrame !== draggingKeyframe.frame && onUpdateKeyframe) {
      onUpdateKeyframe(trackType, draggingKeyframe.frame, newFrame, draggingKeyframe.value);
    }
  }, [draggingKeyframe, trackType, clipStartFrame, clipSpeed, clipDuration, pixelsPerFrame, onUpdateKeyframe]);
  
  const handleKeyframeDragEnd = useCallback(() => {
    setDraggingKeyframe(null);
  }, []);
  
  useEffect(() => {
    if (draggingKeyframe) {
      window.addEventListener('mousemove', handleKeyframeDrag);
      window.addEventListener('mouseup', handleKeyframeDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleKeyframeDrag);
        window.removeEventListener('mouseup', handleKeyframeDragEnd);
      };
    }
  }, [draggingKeyframe, handleKeyframeDrag, handleKeyframeDragEnd]);
  
  // Inline editing
  const handleKeyframeDoubleClick = (e, kf) => {
    e.stopPropagation();
    setEditingKeyframe(kf);
    setEditValues(kf.value || {});
  };
  
  const handleEditSubmit = () => {
    if (editingKeyframe && onUpdateKeyframe) {
      onUpdateKeyframe(trackType, editingKeyframe.frame, editingKeyframe.frame, editValues);
    }
    setEditingKeyframe(null);
  };
  
  const formatValue = (val) => {
    if (!val) return '';
    if (trackType === 'visibility') {
      return val.visible ? `On (${Math.round((val.opacity || 1) * 100)}%)` : 'Off';
    }
    if (typeof val === 'object') {
      return `${val.x?.toFixed(1) || 0}, ${val.y?.toFixed(1) || 0}, ${val.z?.toFixed(1) || 0}`;
    }
    return String(val);
  };
  
  return (
    <div className={`flex h-6 border-b border-slate-800 ${isSelected ? 'ring-1 ring-inset ring-white/30' : ''}`}>
      <div 
        ref={trackRef}
        className={`flex-1 relative ${trackConfig.bgColor} cursor-pointer`}
        style={{ minWidth: totalFrames * pixelsPerFrame }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        title={`${trackConfig.name} track - Click to select, Double-click to add keyframe`}
      >
        {/* Transform keyframe diamonds */}
        {trackKeyframes.map((kf, idx) => (
          <div
            key={`${trackType}-${kf.frame}-${idx}`}
            className={`absolute top-1/2 -translate-y-1/2 z-10 cursor-grab
              ${draggingKeyframe?.frame === kf.frame ? 'cursor-grabbing' : ''}`}
            style={{ left: kf.displayFrame * pixelsPerFrame - 5 }}
            title={`Frame ${kf.frame}: ${formatValue(kf.value)} (drag to move, double-click to edit)`}
            onClick={(e) => {
              e.stopPropagation();
              if (editingKeyframe) return; // Don't select when editing
              onSelectKeyframe?.({ id: kf.id, trackType, frame: kf.frame, value: kf.value });
            }}
            onMouseDown={(e) => {
              // Don't start drag if we're in edit mode
              if (editingKeyframe) {
                e.stopPropagation();
                return;
              }
              handleKeyframeDragStart(e, kf);
            }}
            onDoubleClick={(e) => handleKeyframeDoubleClick(e, kf)}
          >
            <div className={`w-2.5 h-2.5 rotate-45 ${trackConfig.color.replace('text-', 'bg-')} 
              hover:scale-110 transition-all
              ${selectedKeyframe?.id === kf.id && selectedKeyframe?.trackType === trackType ? 'ring-2 ring-white scale-125' : ''}`} 
            />
            
            {/* Inline editor popup */}
            {editingKeyframe?.frame === kf.frame && (
              <div 
                className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-slate-800 border border-slate-600 rounded p-2 shadow-lg z-50 min-w-32"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {trackType === 'visibility' ? (
                  <div className="space-y-2">
                    <label className="text-xs text-slate-400 block">Visibility</label>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditValues({...editValues, visible: true})}
                        className={`flex-1 px-2 py-1 text-xs rounded ${editValues.visible !== false ? 'bg-cyan-600' : 'bg-slate-600'}`}
                      >On</button>
                      <button
                        onClick={() => setEditValues({...editValues, visible: false})}
                        className={`flex-1 px-2 py-1 text-xs rounded ${editValues.visible === false ? 'bg-cyan-600' : 'bg-slate-600'}`}
                      >Off</button>
                    </div>
                    <label className="text-xs text-slate-400 block">Opacity ({Math.round((editValues.opacity ?? 1) * 100)}%)</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round((editValues.opacity ?? 1) * 100)}
                      onChange={(e) => setEditValues({...editValues, opacity: parseInt(e.target.value) / 100})}
                      className="w-full h-4"
                    />
                    <div className="flex gap-1 mt-2">
                      <button onClick={handleEditSubmit} className="flex-1 py-1 text-xs bg-green-600 rounded hover:bg-green-500">Apply</button>
                      <button 
                        onClick={() => {
                          onDeleteKeyframe?.(trackType, kf.frame);
                          setEditingKeyframe(null);
                        }} 
                        className="flex-1 py-1 text-xs bg-red-600 rounded hover:bg-red-500"
                      >Delete</button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-xs text-slate-400 block">{trackConfig.name}</label>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-400 w-4">X</label>
                      <input
                        type="number"
                        step={trackType === 'scale' ? '0.1' : '1'}
                        value={editValues.x ?? 0}
                        onChange={(e) => setEditValues({...editValues, x: parseFloat(e.target.value) || 0})}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') handleEditSubmit();
                          if (e.key === 'Escape') setEditingKeyframe(null);
                        }}
                        className="flex-1 h-6 text-sm bg-slate-700 border border-slate-500 rounded px-2 text-white w-16 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-400 w-4">Y</label>
                      <input
                        type="number"
                        step={trackType === 'scale' ? '0.1' : '1'}
                        value={editValues.y ?? 0}
                        onChange={(e) => setEditValues({...editValues, y: parseFloat(e.target.value) || 0})}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') handleEditSubmit();
                          if (e.key === 'Escape') setEditingKeyframe(null);
                        }}
                        className="flex-1 h-6 text-sm bg-slate-700 border border-slate-500 rounded px-2 text-white w-16 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-400 w-4">Z</label>
                      <input
                        type="number"
                        step={trackType === 'scale' ? '0.1' : '1'}
                        value={editValues.z ?? 0}
                        onChange={(e) => setEditValues({...editValues, z: parseFloat(e.target.value) || 0})}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') handleEditSubmit();
                          if (e.key === 'Escape') setEditingKeyframe(null);
                        }}
                        className="flex-1 h-6 text-sm bg-slate-700 border border-slate-500 rounded px-2 text-white w-16 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div className="flex gap-1 mt-2">
                      <button onClick={handleEditSubmit} className="flex-1 py-1 text-xs bg-green-600 rounded hover:bg-green-500">Apply</button>
                      <button 
                        onClick={() => {
                          onDeleteKeyframe?.(trackType, kf.frame);
                          setEditingKeyframe(null);
                        }} 
                        className="flex-1 py-1 text-xs bg-red-600 rounded hover:bg-red-500"
                      >Delete</button>
                      <button onClick={() => setEditingKeyframe(null)} className="py-1 px-2 text-xs bg-slate-600 rounded hover:bg-slate-500">✕</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        
        {/* Clip region highlight */}
        <div 
          className={`absolute top-0 bottom-0 ${trackConfig.bgColor} border-l border-r border-white/10 pointer-events-none`}
          style={{
            left: clipStartFrame * pixelsPerFrame,
            width: clipDuration * pixelsPerFrame
          }}
        />
        
        {/* Playhead indicator */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-red-500/50 pointer-events-none will-change-transform"
          style={{ transform: `translateX(${currentFrame * pixelsPerFrame}px)` }}
        />
      </div>
    </div>
  );
}

// ============== SHOT TRACK (Camera/Light) ==============
function ShotTrack({ 
  trackType, // 'camera' | 'light'
  keyframes = [],
  totalFrames, 
  pixelsPerFrame, 
  currentFrame,
  isSelected,
  onSelect,
  onAddKeyframe,
  onSelectKeyframe,
  selectedKeyframe,
  onUpdateKeyframe,
  onDeleteKeyframe
}) {
  const trackRef = useRef(null);
  const [editingKeyframe, setEditingKeyframe] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [draggedKeyframe, setDraggedKeyframe] = useState(null);
  
  const trackConfig = SHOT_TRACKS.find(t => t.id === trackType);
  const IconComponent = trackConfig?.icon || Camera;
  
  const handleDoubleClick = (e) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frame = Math.round(x / pixelsPerFrame);
    
    // Add keyframe at clicked position
    const defaultValue = trackType === 'camera' 
      ? { position: { x: 0, y: 2, z: 5 }, rotation: { x: 0, y: 0, z: 0 } }
      : { position: { x: 5, y: 10, z: 5 }, intensity: 1 };
    
    onAddKeyframe?.(trackType, frame, defaultValue);
  };
  
  const handleKeyframeDragStart = (e, kf) => {
    e.stopPropagation();
    setDraggedKeyframe(kf);
    
    const handleMouseMove = (moveEvent) => {
      if (!trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const newFrame = Math.max(0, Math.min(totalFrames, Math.round(x / pixelsPerFrame)));
      
      if (newFrame !== kf.frame) {
        onUpdateKeyframe?.(trackType, kf.frame, newFrame, kf.value);
      }
    };
    
    const handleMouseUp = () => {
      setDraggedKeyframe(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  return (
    <div 
      className={`h-7 relative cursor-pointer ${isSelected ? 'ring-1 ring-amber-500' : ''}`}
      onClick={() => onSelect?.({ type: 'shot', id: trackType, name: trackConfig?.name })}
      onDoubleClick={handleDoubleClick}
      ref={trackRef}
    >
      {/* Track background */}
      <div className={`absolute inset-0 ${trackConfig?.bgColor || 'bg-slate-800/50'} border-b border-slate-700/50`} />
      
      {/* Keyframes */}
      {keyframes.map((kf) => (
        <div
          key={`shot-${trackType}-${kf.frame}`}
          className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full cursor-pointer
            ${selectedKeyframe?.type === 'shot' && selectedKeyframe?.trackType === trackType && selectedKeyframe?.frame === kf.frame
              ? 'ring-2 ring-white scale-125' 
              : ''
            } ${trackConfig?.color || 'text-amber-400'} ${draggedKeyframe?.frame === kf.frame ? 'opacity-50' : ''}`}
          style={{ left: kf.frame * pixelsPerFrame - 6 }}
          onMouseDown={(e) => handleKeyframeDragStart(e, kf)}
          onClick={(e) => {
            e.stopPropagation();
            onSelectKeyframe?.({ type: 'shot', trackType, frame: kf.frame, value: kf.value });
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditingKeyframe(kf);
            setEditValues(kf.value);
          }}
        >
          <Diamond className="w-3 h-3 fill-current" />
        </div>
      ))}
      
      {/* Playhead indicator */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-red-500/50 pointer-events-none will-change-transform"
        style={{ transform: `translateX(${currentFrame * pixelsPerFrame}px)` }}
      />
    </div>
  );
}

// ============== TRACK DROP ZONE ==============
function TrackDropZone({ 
  character, 
  clips, 
  totalFrames, 
  pixelsPerFrame, 
  currentFrame,
  selectedClip,
  onClipClick,
  onClipDoubleClick,
  onClipDragEnd,
  onClipResizeEnd,
  onDropAnimation
}) {
  const trackRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const characterClips = clips.filter(c => c.characterId === character.id);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'animation') {
        const rect = trackRef.current.getBoundingClientRect();
        const dropX = e.clientX - rect.left;
        const dropFrame = Math.max(0, Math.round(dropX / pixelsPerFrame));
        onDropAnimation(character.id, data.animation, dropFrame);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  return (
    <div 
      ref={trackRef}
      className={`h-10 relative ${isDragOver ? 'bg-purple-500/20' : ''}`}
      style={{ minWidth: totalFrames * pixelsPerFrame }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Grid lines */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        {Array.from({ length: Math.ceil(totalFrames / DEFAULT_FPS) }, (_, i) => (
          <div
            key={i}
            className="absolute top-0 bottom-0 w-px bg-slate-600"
            style={{ left: i * DEFAULT_FPS * pixelsPerFrame }}
          />
        ))}
      </div>
      
      {/* Clips */}
      {characterClips.map((clip) => (
        <AnimationClip
          key={clip.id}
          clip={clip}
          pixelsPerFrame={pixelsPerFrame}
          isSelected={selectedClip?.id === clip.id}
          onClick={onClipClick}
          onDoubleClick={onClipDoubleClick}
          onDragEnd={onClipDragEnd}
          onResizeEnd={onClipResizeEnd}
        />
      ))}
      
      {/* Drop indicator */}
      {isDragOver && (
        <div className="absolute inset-0 border-2 border-dashed border-purple-400 rounded pointer-events-none" />
      )}
      
      {/* Playhead */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-red-500/50 pointer-events-none z-10 will-change-transform"
        style={{ transform: `translateX(${currentFrame * pixelsPerFrame}px)` }}
      />
    </div>
  );
}

// ============== MAIN CLIP TIMELINE ==============
export default function ClipTimeline({
  characters = [],
  clips = [],
  onClipsChange,
  selectedClip,
  onSelectClip,
  onClipDoubleClick,
  isPlaying,
  onPlayPause,
  currentFrame,
  onFrameChange,
  totalFrames = 480,
  fps = DEFAULT_FPS,
  bones = [],
  expandedClipId,
  getClipKeyframes,
  // For adding characters to timeline
  onAddCharacterToTimeline,
  // For capturing current pose (passed from parent)
  onCaptureCurrentPose,
  // For capturing character transforms
  onCaptureCharacterTransform,
  // For character selection
  selectedCharacter,
  onSelectCharacter,
  // For track selection
  selectedTrack,
  onSelectTrack,
  // Recording mode
  recordMode, // 'off' | 'all' | 'changes'
  onRecordModeChange,
  // Shot-level controls (camera, light) - independent of clips
  shotSettings = { camera: { position: { x: 0, y: 2, z: 5 }, rotation: { x: 0, y: 0, z: 0 } }, light: { position: { x: 5, y: 10, z: 5 } } },
  shotKeyframes = { camera: [], light: [] },
  onShotSettingsChange,
  onShotKeyframeAdd,
  onShotKeyframeDelete,
  onShotKeyframeUpdate
}) {
  const [pixelsPerFrame, setPixelsPerFrame] = useState(PIXELS_PER_FRAME);
  const [isDragOverTimeline, setIsDragOverTimeline] = useState(false);
  const [selectedKeyframe, setSelectedKeyframe] = useState(null);
  const [localRecordMode, setLocalRecordMode] = useState(recordMode || 'off');
  const timelineRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const labelsContainerRef = useRef(null);
  
  // Sync scroll between labels and tracks (vertical only)
  useEffect(() => {
    const labelsEl = labelsContainerRef.current;
    const tracksEl = scrollContainerRef.current;
    if (!labelsEl || !tracksEl) return;
    
    let isSyncing = false;
    
    const handleLabelsScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      tracksEl.scrollTop = labelsEl.scrollTop;
      requestAnimationFrame(() => { isSyncing = false; });
    };
    
    const handleTracksScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      labelsEl.scrollTop = tracksEl.scrollTop;
      requestAnimationFrame(() => { isSyncing = false; });
    };
    
    labelsEl.addEventListener('scroll', handleLabelsScroll);
    tracksEl.addEventListener('scroll', handleTracksScroll);
    
    return () => {
      labelsEl.removeEventListener('scroll', handleLabelsScroll);
      tracksEl.removeEventListener('scroll', handleTracksScroll);
    };
  }, []);
  
  // Sync local record mode with prop
  useEffect(() => {
    if (recordMode !== undefined) {
      setLocalRecordMode(recordMode);
    }
  }, [recordMode]);
  
  const handleRecordModeChange = (mode) => {
    setLocalRecordMode(mode);
    onRecordModeChange?.(mode);
  };
  
  // Zoom controls
  const handleZoomIn = () => setPixelsPerFrame(prev => Math.min(prev * 1.5, 20));
  const handleZoomOut = () => setPixelsPerFrame(prev => Math.max(prev / 1.5, 1));
  
  // Frame navigation
  const handlePrevFrame = () => onFrameChange(Math.max(0, currentFrame - 1));
  const handleNextFrame = () => onFrameChange(Math.min(totalFrames - 1, currentFrame + 1));
  const handleGoToStart = () => onFrameChange(0);
  const handleGoToEnd = () => onFrameChange(totalFrames - 1);
  
  // Handle clip position change
  const handleClipDragEnd = useCallback((clipId, newStartFrame) => {
    const updatedClips = clips.map(c => 
      c.id === clipId ? { ...c, startFrame: newStartFrame } : c
    );
    
    const sortedClips = [...updatedClips].sort((a, b) => a.startFrame - b.startFrame);
    
    for (let i = 1; i < sortedClips.length; i++) {
      const prevClip = sortedClips[i - 1];
      const currClip = sortedClips[i];
      
      if (currClip.characterId === prevClip.characterId) {
        const prevEnd = prevClip.startFrame + prevClip.duration;
        if (currClip.startFrame < prevEnd) {
          currClip.startFrame = prevEnd;
        }
      }
    }
    
    onClipsChange(sortedClips);
  }, [clips, onClipsChange]);
  
  // Handle clip resize
  const handleClipResizeEnd = useCallback((clipId, newDuration) => {
    const clip = clips.find(c => c.id === clipId);
    if (!clip) return;
    
    const baseDuration = clip.animation?.total_frames || 48;
    const loops = Math.max(1, Math.round(newDuration / baseDuration));
    const actualDuration = loops * baseDuration;
    
    const updatedClips = clips.map(c => 
      c.id === clipId ? { ...c, duration: actualDuration, loops } : c
    );
    
    onClipsChange(updatedClips);
  }, [clips, onClipsChange]);
  
  // Handle animation drop
  const handleDropAnimation = useCallback((characterId, animation, dropFrame) => {
    // Custom (blank) template creates a 10-second clip (240 frames at 24fps)
    const isBlankTemplate = animation.is_blank_template;
    const baseDuration = isBlankTemplate ? 240 : (animation.total_frames || 48);
    
    const newClip = {
      id: `clip-${Date.now()}`,
      characterId,
      animation: {
        ...animation,
        total_frames: baseDuration,
        useBuiltIn: animation.is_builtin || false,
        is_blank_template: isBlankTemplate,
        // Blank template starts with no keyframes
        keyframes: isBlankTemplate ? [] : (animation.keyframes || [])
      },
      startFrame: dropFrame,
      duration: baseDuration,
      loops: 1,
      speed: 1,
      color: isBlankTemplate ? 'bg-amber-600' : undefined
    };
    
    const characterClips = clips.filter(c => c.characterId === characterId);
    const otherClips = clips.filter(c => c.characterId !== characterId);
    
    const pushedClips = characterClips.map(c => {
      if (c.startFrame >= dropFrame) {
        return { ...c, startFrame: c.startFrame + baseDuration };
      }
      const cEnd = c.startFrame + c.duration;
      if (dropFrame < cEnd && dropFrame > c.startFrame) {
        return { ...c, startFrame: c.startFrame + baseDuration };
      }
      return c;
    });
    
    onClipsChange([...otherClips, ...pushedClips, newClip]);
    
    // Show toast feedback
    if (isBlankTemplate) {
      toast.success('Added blank 10s clip - add keyframes to animate');
    }
  }, [clips, onClipsChange]);

  // ============== KEYFRAME HANDLERS ==============
  
  // Select a keyframe
  const handleSelectKeyframe = useCallback((keyframe, boneName) => {
    setSelectedKeyframe({ ...keyframe, boneName });
  }, []);
  
  // Drag a keyframe to a new frame
  const handleDragKeyframe = useCallback((keyframeId, boneName, newLocalFrame) => {
    if (!expandedClipId) return;
    
    const updatedClips = clips.map(clip => {
      if (clip.id !== expandedClipId) return clip;
      
      const keyframes = clip.animation?.keyframes || [];
      const updatedKeyframes = keyframes.map(kf => {
        if (kf.id === keyframeId) {
          return { ...kf, frame: Math.max(0, newLocalFrame) };
        }
        return kf;
      });
      
      // Sort keyframes by frame
      updatedKeyframes.sort((a, b) => a.frame - b.frame);
      
      return {
        ...clip,
        animation: {
          ...clip.animation,
          keyframes: updatedKeyframes
        }
      };
    });
    
    onClipsChange(updatedClips);
  }, [clips, expandedClipId, onClipsChange]);
  
  // Delete a keyframe
  const handleDeleteKeyframe = useCallback((keyframeId, boneName) => {
    if (!expandedClipId) return;
    
    const updatedClips = clips.map(clip => {
      if (clip.id !== expandedClipId) return clip;
      
      const keyframes = clip.animation?.keyframes || [];
      
      // Remove the bone data from the keyframe (not the whole keyframe if other bones exist)
      const updatedKeyframes = keyframes.map(kf => {
        if (kf.id === keyframeId) {
          const newBones = { ...kf.bones };
          delete newBones[boneName];
          
          // If no bones left, mark for removal
          if (Object.keys(newBones).length === 0) {
            return null;
          }
          
          return { ...kf, bones: newBones };
        }
        return kf;
      }).filter(Boolean);
      
      return {
        ...clip,
        animation: {
          ...clip.animation,
          keyframes: updatedKeyframes
        }
      };
    });
    
    onClipsChange(updatedClips);
    setSelectedKeyframe(null);
  }, [clips, expandedClipId, onClipsChange]);
  
  // Add a new keyframe at a specific frame for a bone
  const handleAddKeyframe = useCallback((boneName, localFrame) => {
    if (!expandedClipId || !onCaptureCurrentPose) return;
    
    // Get current pose from the parent component
    const currentPose = onCaptureCurrentPose();
    if (!currentPose || !currentPose[boneName]) return;
    
    const updatedClips = clips.map(clip => {
      if (clip.id !== expandedClipId) return clip;
      
      const keyframes = clip.animation?.keyframes || [];
      
      // Check if a keyframe already exists at this frame
      const existingIdx = keyframes.findIndex(kf => kf.frame === localFrame);
      
      let updatedKeyframes;
      if (existingIdx >= 0) {
        // Update existing keyframe with this bone's data
        updatedKeyframes = keyframes.map((kf, idx) => {
          if (idx === existingIdx) {
            return {
              ...kf,
              bones: {
                ...kf.bones,
                [boneName]: currentPose[boneName]
              }
            };
          }
          return kf;
        });
      } else {
        // Create new keyframe
        const newKeyframe = {
          id: `kf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          frame: localFrame,
          bones: {
            [boneName]: currentPose[boneName]
          }
        };
        updatedKeyframes = [...keyframes, newKeyframe].sort((a, b) => a.frame - b.frame);
      }
      
      return {
        ...clip,
        animation: {
          ...clip.animation,
          keyframes: updatedKeyframes
        }
      };
    });
    
    onClipsChange(updatedClips);
  }, [clips, expandedClipId, onClipsChange, onCaptureCurrentPose]);

  // Handle character drop on timeline (for multi-character)
  const handleTimelineDragOver = (e) => {
    e.preventDefault();
    try {
      // Check if it's a character being dragged
      const types = e.dataTransfer.types;
      if (types.includes('application/json')) {
        setIsDragOverTimeline(true);
      }
    } catch (err) {
      // Ignore drag-over errors silently
    }
  };

  const handleTimelineDragLeave = () => {
    setIsDragOverTimeline(false);
  };

  const handleTimelineDrop = (e) => {
    e.preventDefault();
    setIsDragOverTimeline(false);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'character' && onAddCharacterToTimeline) {
        onAddCharacterToTimeline(data.character);
      }
    } catch (err) {
      console.error('Timeline drop error:', err);
    }
  };
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          onPlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevFrame();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNextFrame();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentFrame, onPlayPause, handlePrevFrame, handleNextFrame]);

  return (
    <div className="bg-slate-800 border-t border-slate-700 flex flex-col flex-shrink-0" data-testid="clip-timeline">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700 bg-slate-850">
        {/* Playback controls */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleGoToStart}>
            <SkipBack size={14} />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handlePrevFrame}>
            <ChevronRight size={14} className="rotate-180" />
          </Button>
          <Button
            variant={isPlaying ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 w-7 p-0"
            onClick={onPlayPause}
            data-testid="timeline-play-pause"
          >
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleNextFrame}>
            <ChevronRight size={14} />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleGoToEnd}>
            <SkipForward size={14} />
          </Button>
        </div>
        
        <div className="h-4 w-px bg-slate-600 mx-1" />
        
        {/* Frame counter */}
        <div className="flex items-center gap-2 text-xs text-slate-400 font-mono min-w-[100px]">
          <span>{String(currentFrame).padStart(3, '0')}</span>
          <span>/</span>
          <span>{totalFrames}</span>
          <span className="text-slate-500">({(currentFrame / fps).toFixed(2)}s)</span>
        </div>
        
        {/* Keyframe controls - show when clip is expanded */}
        {expandedClipId && (
          <>
            <div className="h-4 w-px bg-slate-600 mx-1" />
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/20"
                onClick={() => {
                  if (!onCaptureCurrentPose || bones.length === 0) return;
                  
                  const pose = onCaptureCurrentPose();
                  if (!pose || Object.keys(pose).length === 0) {
                    toast.error('Could not capture current pose');
                    return;
                  }
                  
                  const expandedClip = clips.find(c => c.id === expandedClipId);
                  if (!expandedClip) return;
                  
                  const localFrame = currentFrame - expandedClip.startFrame;
                  if (localFrame < 0 || localFrame >= expandedClip.duration) {
                    toast.error('Playhead must be within clip range');
                    return;
                  }
                  
                  // Build bones object with ALL bone transforms from current pose
                  const allBones = {};
                  bones.forEach(boneName => {
                    if (pose[boneName]) {
                      allBones[boneName] = pose[boneName];
                    }
                  });
                  
                  const boneCount = Object.keys(allBones).length;
                  if (boneCount === 0) {
                    toast.error('No bone data captured');
                    return;
                  }
                  
                  // Check if keyframe exists at this frame
                  const existingKeyframes = expandedClip.animation?.keyframes || [];
                  const isUpdate = existingKeyframes.some(kf => kf.frame === localFrame);
                  
                  // Create/update keyframe for ALL bones at once
                  const updatedClips = clips.map(clip => {
                    if (clip.id !== expandedClipId) return clip;
                    
                    const keyframes = clip.animation?.keyframes || [];
                    const existingIdx = keyframes.findIndex(kf => kf.frame === localFrame);
                    
                    let updatedKeyframes;
                    if (existingIdx >= 0) {
                      // Overwrite existing keyframe with all bone data
                      updatedKeyframes = keyframes.map((kf, idx) => {
                        if (idx === existingIdx) {
                          return {
                            ...kf,
                            bones: allBones // Replace ALL bones with current pose
                          };
                        }
                        return kf;
                      });
                    } else {
                      // Create new keyframe with all bones
                      const newKeyframe = {
                        id: `kf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        frame: localFrame,
                        bones: allBones
                      };
                      updatedKeyframes = [...keyframes, newKeyframe].sort((a, b) => a.frame - b.frame);
                    }
                    
                    return {
                      ...clip,
                      animation: {
                        ...clip.animation,
                        keyframes: updatedKeyframes
                      }
                    };
                  });
                  
                  onClipsChange(updatedClips);
                }}
                title="Add/overwrite keyframe for all bones at current playhead"
              >
                <Diamond size={12} className="mr-1" />
                Add Keyframe
              </Button>
              
              {/* Recording mode buttons */}
              <div className="h-4 w-px bg-slate-600 mx-1" />
              
              <Button
                variant={localRecordMode === 'all' ? 'secondary' : 'ghost'}
                size="sm"
                className={`h-7 px-2 text-xs ${localRecordMode === 'all' 
                  ? 'bg-red-600 text-white hover:bg-red-500' 
                  : 'text-red-400 hover:text-red-300 hover:bg-red-500/20'}`}
                onClick={() => handleRecordModeChange(localRecordMode === 'all' ? 'off' : 'all')}
                title="Record All: Automatically keyframe all tracks when any value changes"
              >
                <CircleDot size={12} className="mr-1" />
                Record All
              </Button>
              
              <Button
                variant={localRecordMode === 'changes' ? 'secondary' : 'ghost'}
                size="sm"
                className={`h-7 px-2 text-xs ${localRecordMode === 'changes' 
                  ? 'bg-orange-600 text-white hover:bg-orange-500' 
                  : 'text-orange-400 hover:text-orange-300 hover:bg-orange-500/20'}`}
                onClick={() => handleRecordModeChange(localRecordMode === 'changes' ? 'off' : 'changes')}
                title="Record Changes: Only keyframe the track that was modified"
              >
                <Circle size={12} className="mr-1" />
                Record Changes
              </Button>
              
              {selectedKeyframe && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/20"
                  onClick={() => {
                    // Handle different keyframe types
                    if (selectedKeyframe.type === 'yOffset') {
                      // Delete Y-Offset keyframe
                      const updatedClips = clips.map(c => {
                        if (c.id !== expandedClipId) return c;
                        const yOffsetKfs = (c.animation?.rootMotion?.yOffsetKeyframes || [])
                          .filter(kf => kf.frame !== selectedKeyframe.frame);
                        return {
                          ...c,
                          animation: {
                            ...c.animation,
                            rootMotion: {
                              ...c.animation?.rootMotion,
                              yOffsetKeyframes: yOffsetKfs
                            }
                          }
                        };
                      });
                      onClipsChange(updatedClips);
                      setSelectedKeyframe(null);
                    } else if (selectedKeyframe.trackType) {
                      // Delete Transform keyframe (position, rotation, scale, visibility)
                      const updatedClips = clips.map(c => {
                        if (c.id !== expandedClipId) return c;
                        const transformKfs = (c.animation?.transformKeyframes || [])
                          .filter(kf => !(kf.trackType === selectedKeyframe.trackType && kf.frame === selectedKeyframe.frame));
                        return { ...c, animation: { ...c.animation, transformKeyframes: transformKfs }};
                      });
                      onClipsChange(updatedClips);
                      setSelectedKeyframe(null);
                    } else if (selectedKeyframe.boneName) {
                      // Delete Bone keyframe
                      handleDeleteKeyframe(selectedKeyframe.id, selectedKeyframe.boneName);
                    }
                  }}
                  title="Delete selected keyframe"
                >
                  <Trash2 size={12} className="mr-1" />
                  Delete
                </Button>
              )}
            </div>
          </>
        )}
        
        <div className="flex-1" />
        
        {/* Zoom controls */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleZoomOut}>
            <Minus size={12} />
          </Button>
          <span className="text-xs text-slate-400 w-12 text-center">{Math.round(pixelsPerFrame * 25)}%</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleZoomIn}>
            <Plus size={12} />
          </Button>
        </div>
      </div>
      
      {/* Timeline content - fixed height area */}
      <div 
        className="flex min-h-[140px] max-h-[350px] overflow-hidden"
        onDragOver={handleTimelineDragOver}
        onDragLeave={handleTimelineDragLeave}
        onDrop={handleTimelineDrop}
      >
        {/* Fixed track labels column */}
        <div ref={labelsContainerRef} className="flex-shrink-0 bg-slate-850 border-r border-slate-700 overflow-y-auto" style={{ width: TRACK_LABEL_WIDTH }}>
          {/* Ruler label area */}
          <div className="h-6 border-b border-slate-700 flex items-center px-2">
            <span className="text-xs text-slate-500">Tracks</span>
          </div>
          
          {/* Shot Settings Section (Camera & Light) */}
          <div className="border-b border-slate-600">
            <div className="h-7 flex items-center gap-2 px-2 bg-slate-800/50">
              <Camera className="w-3 h-3 text-amber-400" />
              <span className="text-xs text-slate-400 font-medium">Shot Settings</span>
            </div>
            
            {/* Camera track label */}
            <button
              onClick={() => onSelectTrack?.({ type: 'shot', id: 'camera', name: 'Camera' })}
              className={`w-full h-7 flex items-center gap-2 pl-6 pr-2 text-left transition-colors border-b border-slate-700/50
                ${selectedTrack?.type === 'shot' && selectedTrack?.id === 'camera'
                  ? 'bg-amber-600/20 border-l-2 border-l-amber-500' 
                  : 'hover:bg-slate-800/50'
                }`}
            >
              <Camera className="w-3 h-3 text-amber-400" />
              <span className="text-xs text-slate-300">Camera</span>
            </button>
            
            {/* Light track label */}
            <button
              onClick={() => onSelectTrack?.({ type: 'shot', id: 'light', name: 'Light' })}
              className={`w-full h-7 flex items-center gap-2 pl-6 pr-2 text-left transition-colors
                ${selectedTrack?.type === 'shot' && selectedTrack?.id === 'light'
                  ? 'bg-yellow-600/20 border-l-2 border-l-yellow-500' 
                  : 'hover:bg-slate-800/50'
                }`}
            >
              <Sun className="w-3 h-3 text-yellow-400" />
              <span className="text-xs text-slate-300">Light</span>
            </button>
          </div>
          
          {/* Character labels */}
          {characters.length === 0 ? (
            <div className={`h-16 flex items-center justify-center text-xs text-slate-500 ${isDragOverTimeline ? 'bg-purple-500/20' : ''}`}>
              {isDragOverTimeline ? 'Drop character here' : 'Drag character here'}
            </div>
          ) : (
            characters.map((character) => {
              // Check if any clip for this character is expanded
              const characterClips = clips.filter(c => c.characterId === character.id);
              const expandedClip = characterClips.find(c => c.id === expandedClipId);
              const clipKeyframes = expandedClip && getClipKeyframes ? getClipKeyframes(expandedClip.id) : [];
              
              return (
                <div key={character.id}>
                  {/* Main character row - clickable to select character */}
                  <button
                    onClick={() => onSelectCharacter && onSelectCharacter(character)}
                    className={`w-full h-10 flex items-center gap-2 px-2 border-b border-slate-700 text-left transition-colors
                      ${selectedCharacter?.id === character.id 
                        ? 'bg-blue-600/20 border-l-2 border-l-blue-500' 
                        : 'hover:bg-slate-800/50'
                      }`}
                    data-testid={`character-track-${character.id}`}
                  >
                    <div className="w-6 h-6 rounded bg-slate-700 overflow-hidden flex-shrink-0">
                      {character.thumbnail_url || character.source_image_url ? (
                        <img 
                          src={character.thumbnail_url || character.source_image_url} 
                          alt={character.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-full h-full p-1 text-slate-500" />
                      )}
                    </div>
                    <span className={`text-xs font-medium truncate ${
                      selectedCharacter?.id === character.id ? 'text-blue-300' : 'text-white'
                    }`}>{character.name}</span>
                    {expandedClip && (
                      <span className="text-xs text-purple-400 ml-auto">▼ {bones.length} bones</span>
                    )}
                  </button>
                  
                  {/* Expanded track labels - Transform tracks + Y Offset + Bones */}
                  {expandedClip && (
                    <>
                      {/* Character Transform track labels */}
                      {TRANSFORM_TRACKS.map((track) => {
                        const Icon = track.icon;
                        const isSelected = selectedTrack?.type === 'transform' && selectedTrack?.id === track.id;
                        return (
                          <button
                            key={track.id}
                            onClick={() => onSelectTrack?.({ type: 'transform', id: track.id, name: track.name })}
                            className={`w-full h-6 flex items-center gap-2 px-4 border-b border-slate-800 ${track.bgColor} 
                              hover:brightness-125 transition-all cursor-pointer
                              ${isSelected ? 'ring-1 ring-inset ring-white/50' : ''}`}
                          >
                            <Icon size={12} className={track.color} />
                            <span className={`text-xs font-medium truncate ${track.color}`}>{track.name}</span>
                          </button>
                        );
                      })}
                      
                      {/* Y Offset track label */}
                      <button
                        onClick={() => onSelectTrack?.({ type: 'yOffset', id: 'yOffset', name: 'Y Offset' })}
                        className={`w-full h-6 flex items-center px-4 border-b border-slate-800 bg-amber-900/30 
                          hover:brightness-125 transition-all cursor-pointer
                          ${selectedTrack?.id === 'yOffset' ? 'ring-1 ring-inset ring-white/50' : ''}`}
                      >
                        <span className="text-xs text-amber-400 font-medium truncate">Y Offset</span>
                      </button>
                      
                      {/* All bone track labels - clickable */}
                      {bones.map((boneName) => {
                        const isSelected = selectedTrack?.type === 'bone' && selectedTrack?.id === boneName;
                        return (
                          <button
                            key={boneName}
                            onClick={() => onSelectTrack?.({ type: 'bone', id: boneName, name: boneName })}
                            className={`w-full h-6 flex items-center px-4 border-b border-slate-800 bg-slate-900/50 
                              hover:bg-slate-800/50 transition-all cursor-pointer
                              ${isSelected ? 'ring-1 ring-inset ring-white/50 bg-slate-800/80' : ''}`}
                          >
                            <span className={`text-xs truncate ${isSelected ? 'text-white' : 'text-slate-500'}`}>{boneName}</span>
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
        
        {/* Scrollable timeline tracks */}
        <div ref={scrollContainerRef} className="flex-1 overflow-x-auto overflow-y-auto">
          <div ref={timelineRef} style={{ minWidth: totalFrames * pixelsPerFrame }}>
            {/* Ruler */}
            <TimelineRuler
              totalFrames={totalFrames}
              fps={fps}
              currentFrame={currentFrame}
              pixelsPerFrame={pixelsPerFrame}
              onFrameClick={onFrameChange}
            />
            
            {/* Shot Settings Tracks (Camera & Light) */}
            <div className="border-b border-slate-600">
              {/* Shot Settings header row */}
              <div className="h-7 bg-slate-800/50 border-b border-slate-700/50" />
              
              {/* Camera track */}
              <ShotTrack
                trackType="camera"
                keyframes={shotKeyframes?.camera || []}
                totalFrames={totalFrames}
                pixelsPerFrame={pixelsPerFrame}
                currentFrame={currentFrame}
                isSelected={selectedTrack?.type === 'shot' && selectedTrack?.id === 'camera'}
                onSelect={() => onSelectTrack?.({ type: 'shot', id: 'camera', name: 'Camera' })}
                onAddKeyframe={(trackType, frame, value) => onShotKeyframeAdd?.(trackType, frame, value)}
                onSelectKeyframe={setSelectedKeyframe}
                selectedKeyframe={selectedKeyframe}
                onUpdateKeyframe={(trackType, oldFrame, newFrame, value) => onShotKeyframeUpdate?.(trackType, oldFrame, newFrame, value)}
                onDeleteKeyframe={(trackType, frame) => onShotKeyframeDelete?.(trackType, frame)}
              />
              
              {/* Light track */}
              <ShotTrack
                trackType="light"
                keyframes={shotKeyframes?.light || []}
                totalFrames={totalFrames}
                pixelsPerFrame={pixelsPerFrame}
                currentFrame={currentFrame}
                isSelected={selectedTrack?.type === 'shot' && selectedTrack?.id === 'light'}
                onSelect={() => onSelectTrack?.({ type: 'shot', id: 'light', name: 'Light' })}
                onAddKeyframe={(trackType, frame, value) => onShotKeyframeAdd?.(trackType, frame, value)}
                onSelectKeyframe={setSelectedKeyframe}
                selectedKeyframe={selectedKeyframe}
                onUpdateKeyframe={(trackType, oldFrame, newFrame, value) => onShotKeyframeUpdate?.(trackType, oldFrame, newFrame, value)}
                onDeleteKeyframe={(trackType, frame) => onShotKeyframeDelete?.(trackType, frame)}
              />
            </div>
            
            {/* Character tracks */}
            {characters.length === 0 ? (
              <div className={`h-16 flex items-center justify-center text-slate-500 text-sm border-b border-slate-700 ${isDragOverTimeline ? 'bg-purple-500/20' : ''}`}>
                {isDragOverTimeline ? (
                  <span className="text-purple-400">Drop to add character track</span>
                ) : (
                  <span>Drag a character from the library to add a track</span>
                )}
              </div>
            ) : (
              characters.map((character) => {
                const characterClips = clips.filter(c => c.characterId === character.id);
                const expandedClip = characterClips.find(c => c.id === expandedClipId);
                const clipKeyframes = expandedClip && getClipKeyframes ? getClipKeyframes(expandedClip.id) : [];
                
                return (
                  <div key={character.id} className="border-b border-slate-700">
                    {/* Main clip track */}
                    <TrackDropZone
                      character={character}
                      clips={clips}
                      totalFrames={totalFrames}
                      pixelsPerFrame={pixelsPerFrame}
                      currentFrame={currentFrame}
                      selectedClip={selectedClip}
                      onClipClick={onSelectClip}
                      onClipDoubleClick={onClipDoubleClick}
                      onClipDragEnd={handleClipDragEnd}
                      onClipResizeEnd={handleClipResizeEnd}
                      onDropAnimation={handleDropAnimation}
                    />
                    
                    {/* Expanded: Transform tracks + Y Offset track + all bone tracks */}
                    {expandedClip && (
                      <>
                        {/* Character Transform tracks (Position, Rotation, Scale, Visibility) */}
                        {TRANSFORM_TRACKS.map((track) => (
                          <TransformTrack
                            key={track.id}
                            trackType={track.id}
                            keyframes={expandedClip.animation?.transformKeyframes || []}
                            clipStartFrame={expandedClip.startFrame}
                            clipDuration={expandedClip.duration}
                            clipSpeed={expandedClip.speed || 1}
                            totalFrames={totalFrames}
                            pixelsPerFrame={pixelsPerFrame}
                            currentFrame={currentFrame}
                            isSelected={selectedTrack?.type === 'transform' && selectedTrack?.id === track.id}
                            onSelect={(trackId) => onSelectTrack?.({ type: 'transform', id: trackId, name: track.name })}
                            onAddKeyframe={(trackId, frame) => {
                              // Add transform keyframe with default value
                              const defaultVal = trackId === 'scale' ? { x: 1, y: 1, z: 1 } :
                                                 trackId === 'visibility' ? { visible: true, opacity: 1 } :
                                                 { x: 0, y: 0, z: 0 };
                              const updatedClips = clips.map(c => {
                                if (c.id !== expandedClip.id) return c;
                                const transformKfs = [...(c.animation?.transformKeyframes || [])];
                                transformKfs.push({
                                  id: `transform-${trackId}-${frame}-${Date.now()}`,
                                  frame,
                                  trackType: trackId,
                                  value: defaultVal,
                                  transforms: { [trackId]: defaultVal }
                                });
                                transformKfs.sort((a, b) => a.frame - b.frame);
                                return { ...c, animation: { ...c.animation, transformKeyframes: transformKfs }};
                              });
                              onClipsChange(updatedClips);
                            }}
                            onSelectKeyframe={setSelectedKeyframe}
                            selectedKeyframe={selectedKeyframe}
                            onUpdateKeyframe={(trackId, oldFrame, newFrame, newValue) => {
                              const updatedClips = clips.map(c => {
                                if (c.id !== expandedClip.id) return c;
                                const transformKfs = (c.animation?.transformKeyframes || []).map(kf => {
                                  if (kf.trackType === trackId && kf.frame === oldFrame) {
                                    return { ...kf, frame: newFrame, value: newValue, transforms: { [trackId]: newValue }};
                                  }
                                  return kf;
                                });
                                transformKfs.sort((a, b) => a.frame - b.frame);
                                return { ...c, animation: { ...c.animation, transformKeyframes: transformKfs }};
                              });
                              onClipsChange(updatedClips);
                            }}
                            onDeleteKeyframe={(trackId, frame) => {
                              const updatedClips = clips.map(c => {
                                if (c.id !== expandedClip.id) return c;
                                const transformKfs = (c.animation?.transformKeyframes || [])
                                  .filter(kf => !(kf.trackType === trackId && kf.frame === frame));
                                return { ...c, animation: { ...c.animation, transformKeyframes: transformKfs }};
                              });
                              onClipsChange(updatedClips);
                            }}
                          />
                        ))}
                        
                        {/* Y Offset track */}
                        <YOffsetTrack
                          yOffsetKeyframes={expandedClip.animation?.rootMotion?.yOffsetKeyframes || []}
                          clipStartFrame={expandedClip.startFrame}
                          clipDuration={expandedClip.duration}
                          clipSpeed={expandedClip.speed || 1}
                          totalFrames={totalFrames}
                          pixelsPerFrame={pixelsPerFrame}
                          currentFrame={currentFrame}
                          isSelected={selectedTrack?.type === 'yOffset'}
                          onSelect={() => onSelectTrack?.({ type: 'yOffset', id: 'yOffset', name: 'Y Offset' })}
                          onAddKeyframe={(frame, offset) => {
                            // Add Y offset keyframe through parent
                            const updatedClips = clips.map(c => {
                              if (c.id !== expandedClip.id) return c;
                              const yOffsetKfs = [...(c.animation?.rootMotion?.yOffsetKeyframes || [])];
                              const existingIdx = yOffsetKfs.findIndex(kf => kf.frame === frame);
                              if (existingIdx >= 0) {
                                yOffsetKfs[existingIdx] = { frame, offset };
                              } else {
                                yOffsetKfs.push({ frame, offset });
                                yOffsetKfs.sort((a, b) => a.frame - b.frame);
                              }
                              return {
                                ...c,
                                animation: {
                                  ...c.animation,
                                  rootMotion: {
                                    ...c.animation?.rootMotion,
                                    yOffsetKeyframes: yOffsetKfs
                                  }
                                }
                              };
                            });
                            onClipsChange(updatedClips);
                          }}
                          onSelectKeyframe={setSelectedKeyframe}
                          selectedKeyframe={selectedKeyframe}
                          onDeleteKeyframe={(frame) => {
                            const updatedClips = clips.map(c => {
                              if (c.id !== expandedClip.id) return c;
                              const yOffsetKfs = (c.animation?.rootMotion?.yOffsetKeyframes || [])
                                .filter(kf => kf.frame !== frame);
                              return {
                                ...c,
                                animation: {
                                  ...c.animation,
                                  rootMotion: {
                                    ...c.animation?.rootMotion,
                                    yOffsetKeyframes: yOffsetKfs
                                  }
                                }
                              };
                            });
                            onClipsChange(updatedClips);
                          }}
                          onUpdateKeyframe={(oldFrame, newFrame, newOffset) => {
                            const updatedClips = clips.map(c => {
                              if (c.id !== expandedClip.id) return c;
                              const yOffsetKfs = (c.animation?.rootMotion?.yOffsetKeyframes || []).map(kf => {
                                if (kf.frame === oldFrame) {
                                  return { frame: newFrame, offset: newOffset };
                                }
                                return kf;
                              });
                              yOffsetKfs.sort((a, b) => a.frame - b.frame);
                              return {
                                ...c,
                                animation: {
                                  ...c.animation,
                                  rootMotion: {
                                    ...c.animation?.rootMotion,
                                    yOffsetKeyframes: yOffsetKfs
                                  }
                                }
                              };
                            });
                            onClipsChange(updatedClips);
                          }}
                        />
                        
                        {/* All bone tracks */}
                        {bones.map((boneName) => (
                          <BoneTrack
                            key={boneName}
                            boneName={boneName}
                            keyframes={clipKeyframes}
                            clipStartFrame={expandedClip.startFrame}
                            clipDuration={expandedClip.duration}
                            clipSpeed={expandedClip.speed || 1}
                            totalFrames={totalFrames}
                            pixelsPerFrame={pixelsPerFrame}
                            currentFrame={currentFrame}
                            selectedKeyframe={selectedKeyframe}
                            onSelectKeyframe={handleSelectKeyframe}
                            onDragKeyframe={handleDragKeyframe}
                            onDeleteKeyframe={handleDeleteKeyframe}
                            onAddKeyframe={handleAddKeyframe}
                          />
                        ))}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
